import { useDispatch, useSelector } from "react-redux";
import {
  EXLY_ACCOUNT__SET_CART,
  EXLY_ACCOUNT__UPDATE_CART,
} from "../redux/actions";
import { CHANGE_STATES } from "features/Common/modules/Form/constants/Form.constants";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { baseApiClient } from "data/modules/api/api.client";
import { UPDATE_CART_API } from "../account.apis";
import { navigateTo } from "utils/urlUtils";
import { app_route_keys } from "constants/urlPaths";
import { logError } from "utils/error";
import { is_empty } from "features/Common/utils/common.utils";
import { buildCartApiItem, getActiveCartItems } from "../utils/cart.utils";

export const useCart = () => {
  const { notify } = useNotifications();
  const cart = useSelector((state) => state.myAccount.cart);
  const dispatch = useDispatch();

  const isProcessing = (feature_key) =>
    cart[feature_key] ? cart[feature_key]?.processing : false;

  const getItemQuantityInCart = (feature_key) =>
    cart[feature_key] ? cart[feature_key]?.quantity : 0;

  const getCartItemsCount = () =>
    getActiveCartItems(cart).reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );

  const clearCart = async () => {
    const activeItems = getActiveCartItems(cart);

    if (checkIsArrayEmpty(activeItems)) {
      dispatch({ type: EXLY_ACCOUNT__SET_CART, payload: {} });
      return;
    }

    const previousCart = { ...cart };
    dispatch({ type: EXLY_ACCOUNT__SET_CART, payload: {} });

    const itemsWithUuid = activeItems.filter((item) => item.uuid);

    if (!itemsWithUuid.length) return;

    try {
      await Promise.all(
        itemsWithUuid.map((item) =>
          baseApiClient.post(UPDATE_CART_API, {
            item: buildCartApiItem({
              ...item,
              change_state: CHANGE_STATES.deleted,
            }),
          })
        )
      );
    } catch (error) {
      dispatch({ type: EXLY_ACCOUNT__SET_CART, payload: previousCart });

      logError({
        error,
        occuredAt: "src/ui/pages/Account/hooks/useCart.js",
        when: "Error while clearing cart",
        severity: "blocker",
      });

      notify(
        error?.body?.message ?? "Something went wrong!",
        notification_color_keys.error
      );

      throw error;
    }
  };

  const buyNowAddToCart = async (item) => {
    await clearCart();
    return addItemToCart(item);
  };

  const addItemToCart = (item) => {
    if (is_empty(item?.feature_key))
      throw new Error("Invalid item in addItemToCart");

    const { feature_key } = item;
    if (isProcessing(feature_key)) return;

    const existingItem = cart[feature_key];
    const quantity =
      item.quantity ?? (existingItem?.quantity ? existingItem.quantity : 0) + 1;
    const change_state = existingItem?.uuid
      ? CHANGE_STATES.updated
      : CHANGE_STATES.added;

    const cartItem = {
      ...existingItem,
      ...item,
      feature_key,
      processing: true,
      quantity,
      change_state,
    };

    const nextCart = {
      ...cart,
      [feature_key]: cartItem,
    };

    dispatch({
      type: EXLY_ACCOUNT__SET_CART,
      payload: nextCart,
    });

    return updateCart({ cartItem, previousCart: cart });
  };

  const removeItemFromCart = (item) => {
    if (
      is_empty(item?.feature_key) ||
      is_empty(getItemQuantityInCart(item?.feature_key))
    )
      throw new Error("Invalid item in removeItemFromCart");

    const { feature_key } = item;
    if (isProcessing(feature_key)) return;
    const quantity = getItemQuantityInCart(feature_key) - 1;

    // if quantity is 0, remove from redux also
    if (is_empty(quantity)) {
      return deleteItemFromCart(feature_key);
    }

    const change_state = cart[feature_key]?.uuid
      ? is_empty(quantity)
        ? CHANGE_STATES.deleted
        : CHANGE_STATES.updated
      : cart[feature_key]?.change_state;

    const payload = {
      ...cart,
      [feature_key]: {
        ...item,
        processing: true,
        quantity,
        change_state,
      },
    };
    dispatch({
      type: EXLY_ACCOUNT__SET_CART,
      payload,
    });
    return updateCart({
      cartItem: payload[feature_key],
      previousCart: cart,
    });
  };

  const deleteItemFromCart = (feature_key) => {
    const item = { ...cart[feature_key] };
    if (is_empty(item)) throw new Error("Item not in cart deleteItemFromCart");

    if (isProcessing(feature_key)) return;

    let payload;
    if (cart[feature_key].uuid) {
      payload = {
        ...cart,
        [feature_key]: {
          ...item,
          processing: true,
          change_state: CHANGE_STATES.deleted,
        },
      };
    }
    return updateCart({
      cartItem: payload[feature_key],
      updateRedux: false,
    }).then(() => {
      payload = { ...cart };
      delete payload[feature_key];
      dispatch({
        type: EXLY_ACCOUNT__SET_CART,
        payload,
      });
    });
  };

  const updateCart = async ({
    cartItem,
    updateRedux = true,
    previousCart = cart,
  }) => {
    const { feature_key: featureKey } = cartItem ?? {};
    const initialFeatureData = previousCart?.[featureKey];

    try {
      if (is_empty(featureKey))
        throw new Error("Invalid cartItem in updateCart");
      const response = await baseApiClient.post(UPDATE_CART_API, {
        item: buildCartApiItem(cartItem),
      });
      if (!updateRedux) return;
      const cartItemData = response?.data?.data ?? response?.data;
      if (!is_empty(cartItemData?.uuid)) {
        const reduxPayload = {
          [featureKey]: {
            ...initialFeatureData,
            ...cartItem,
            processing: false,
            uuid: cartItemData.uuid,
          },
        };
        dispatch({
          type: EXLY_ACCOUNT__UPDATE_CART,
          payload: reduxPayload,
        });
      }
    } catch (error) {
      const errMsg = error?.body?.message ?? "Something went wrong!";

      logError({
        error,
        occuredAt: "src/ui/pages/Account/hooks/useCart.js",
        when: "Error while updating cart",
        severity: "blocker",
      });

      notify(errMsg, notification_color_keys.error);

      dispatch({
        type: EXLY_ACCOUNT__SET_CART,
        payload: previousCart,
      });
    }
  };

  const handleGoToCart = ({ overrideCartEmptyCheck = false }) => {
    if (is_empty(getCartItemsCount()) && !overrideCartEmptyCheck)
      return notify("Cart is empty!", notification_color_keys.info);
    navigateTo(app_route_keys.cart);
  };

  return {
    addItemToCart,
    buyNowAddToCart,
    clearCart,
    getItemQuantityInCart,
    removeItemFromCart,
    getCartItemsCount,
    deleteItemFromCart,
    handleGoToCart,
    isProcessing,
    updateCart,
    cartList: is_empty(cart)
      ? []
      : Object.values(cart).filter(
          (item) => item.change_state !== CHANGE_STATES.deleted
        ),
  };
};
